import { createContext, ReactNode, useContext, useMemo } from 'react';
import { useAuth0, User } from '@auth0/auth0-react';

interface AtlasUser extends User {
  id: string;
}

interface AuthUserContextValue {
  user: AtlasUser;
  isLoading: boolean;
  isAuthenticated: boolean;
  logout: () => void;
}

const AuthUserContext = createContext<AuthUserContextValue | undefined>(undefined);

export const useAuthUser = () => {
  const context = useContext(AuthUserContext);
  if (!context) {
    throw new Error('useAuthUser must be used within an AuthUserProvider');
  }
  return context;
};

export const AuthUserProvider = ({ children }: { children: ReactNode }) => {
  const { user, isLoading, isAuthenticated, logout } = useAuth0();

  const value: AuthUserContextValue = useMemo(
    () => ({
      user: {
        ...user,
        id: user?.['https://crossnokaye.com/atlasUserIdentifier'] ?? '',
      },
      isLoading,
      isAuthenticated,
      logout: () => logout({ logoutParams: { returnTo: window.location.origin + '/admin' } }),
    }),
    [user, isLoading, isAuthenticated, logout]
  );

  if (!user) {
    return <div>{'Loading...'}</div>;
  }

  return <AuthUserContext.Provider value={value}>{children}</AuthUserContext.Provider>;
};
